import { supabase } from "~/integrations/supabase/client";
import type {
  Hallway,
  HallwayInventoryData,
  Position,
  PositionInventoryData,
  Rack,
  RackInventoryData,
  Warehouse,
  WarehouseInventoryData,
  WmsInventory,
} from "~/types/wms";

const BATCH_SIZE = 100;

/**
 * Runs a Supabase `.in()` query in batches to avoid URL length limits.
 * Splits the IDs array into chunks and merges the results.
 */
async function batchedInQuery<T>(
  table: string,
  select: string,
  column: string,
  ids: string[]
): Promise<T[]> {
  if (ids.length === 0) return [];
  if (ids.length <= BATCH_SIZE) {
    const { data, error } = await supabase
      .from(table)
      .select(select)
      .in(column, ids);
    if (error) throw error;
    return (data || []) as T[];
  }

  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    chunks.push(ids.slice(i, i + BATCH_SIZE));
  }

  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const { data, error } = await supabase
        .from(table)
        .select(select)
        .in(column, chunk);
      if (error) throw error;
      return (data || []) as T[];
    })
  );

  return results.flat();
}

export interface PositionInventory {
  position: Position;
  inventory: WmsInventory[];
  totalBoxes: number;
  reservedBoxes: number;
  availableBoxes: number;
  hasStock: boolean;
}

export interface PositionInventoryDetail {
  position: Position;
  inventory: WmsInventory[];
}

export const inventoryService = {
  /**
   * Get all positions in a rack with their inventory data
   * Used for the visual rack grid display
   */
  async getInventoryByRack(rackId: string): Promise<PositionInventory[]> {
    // First, get all positions for the rack
    const { data: positions, error: positionsError } = await supabase
      .from("wms_positions")
      .select("*")
      .eq("rack_id", rackId)
      .order("level", { ascending: false }) // Higher levels first (top of rack)
      .order("code", { ascending: true });

    if (positionsError) throw positionsError;

    if (!positions || positions.length === 0) {
      return [];
    }

    // Get all inventory for these positions (batched to avoid URL length limits)
    const positionIds = positions.map((p) => p.id);
    const inventory = await batchedInQuery<WmsInventory>(
      "wms_inventory",
      `*, product:products(id, short_name), pallet:wms_pallets(id, code, barcode, lot_number)`,
      "position_id",
      positionIds
    );

    // Group inventory by position
    const inventoryByPosition = new Map<string, WmsInventory[]>();
    for (const inv of inventory) {
      const existing = inventoryByPosition.get(inv.position_id) || [];
      existing.push(inv);
      inventoryByPosition.set(inv.position_id, existing);
    }

    // Build the result
    // NEUTRALIZED: reserved_boxes ignored - availableBoxes = totalBoxes
    const result: PositionInventory[] = positions.map((position) => {
      const positionInventory = inventoryByPosition.get(position.id) || [];
      const totalBoxes = positionInventory.reduce(
        (sum, inv) => sum + (inv.box_count || 0),
        0
      );
      const reservedBoxes = 0; // NEUTRALIZED

      return {
        position: position as Position,
        inventory: positionInventory,
        totalBoxes,
        reservedBoxes,
        availableBoxes: totalBoxes,
        hasStock: totalBoxes > 0,
      };
    });

    return result;
  },

  /**
   * Get detailed inventory for a single position
   * Used when clicking on a position in the grid
   * Returns position details along with inventory (even if empty)
   */
  async getPositionInventory(
    positionId: string
  ): Promise<PositionInventoryDetail> {
    // First, get the position details
    const { data: position, error: positionError } = await supabase
      .from("wms_positions")
      .select("*")
      .eq("id", positionId)
      .single();

    if (positionError) throw positionError;

    // Then get the inventory for this position
    const { data: inventory, error: inventoryError } = await supabase
      .from("wms_inventory")
      .select(
        `
        *,
        product:products(id, short_name),
        pallet:wms_pallets(id, code, barcode, lot_number)
      `
      )
      .eq("position_id", positionId)
      .order("expiration_date", { ascending: true }); // FEFO order

    if (inventoryError) throw inventoryError;

    return {
      position: position as Position,
      inventory: (inventory || []) as WmsInventory[],
    };
  },

  /**
   * Get inventory summary by product (for future use)
   */
  async getInventorySummary(warehouseId?: string): Promise<
    {
      productId: string;
      productName: string;
      totalBoxes: number;
      reservedBoxes: number;
      availableBoxes: number;
      positionCount: number;
    }[]
  > {
    let query = supabase.from("wms_inventory").select(`
        product_id,
        box_count,
        reserved_boxes,
        position:wms_positions!inner(
          id,
          rack:wms_racks!inner(
            hallway:wms_hallways!inner(
              warehouse_id
            )
          )
        ),
        product:products(short_name)
      `);

    if (warehouseId) {
      query = query.eq("position.rack.hallway.warehouse_id", warehouseId);
    }

    const { data, error } = await query;
    if (error) throw error;

    // Group by product
    const byProduct = new Map<
      string,
      {
        productName: string;
        totalBoxes: number;
        reservedBoxes: number;
        positionIds: Set<string>;
      }
    >();

    // NEUTRALIZED: reserved_boxes ignored
    for (const inv of data || []) {
      const productId = inv.product_id;
      const existing = byProduct.get(productId) || {
        productName: inv.product?.short_name || "",
        totalBoxes: 0,
        reservedBoxes: 0, // NEUTRALIZED: always 0
        positionIds: new Set<string>(),
      };

      existing.totalBoxes += inv.box_count || 0;
      // NEUTRALIZED: reservedBoxes stays 0
      existing.positionIds.add(inv.position?.id || "");

      byProduct.set(productId, existing);
    }

    return Array.from(byProduct.entries()).map(([productId, data]) => ({
      productId,
      productName: data.productName,
      totalBoxes: data.totalBoxes,
      reservedBoxes: 0, // NEUTRALIZED
      availableBoxes: data.totalBoxes, // NEUTRALIZED: same as total
      positionCount: data.positionIds.size,
    }));
  },

  /**
   * Get full warehouse inventory with hierarchy: warehouse > hallways > racks > positions
   * Used for the full warehouse grid visualization
   */
  async getInventoryByWarehouse(
    warehouseId: string
  ): Promise<WarehouseInventoryData> {
    // 1. Get warehouse
    const { data: warehouse, error: warehouseError } = await supabase
      .from("wms_warehouses")
      .select("*")
      .eq("id", warehouseId)
      .single();

    if (warehouseError) throw warehouseError;

    // 2. Get all hallways for this warehouse
    const { data: hallways, error: hallwaysError } = await supabase
      .from("wms_hallways")
      .select("*")
      .eq("warehouse_id", warehouseId)
      .eq("is_active", true)
      .order("sequence", { ascending: true });

    if (hallwaysError) throw hallwaysError;

    if (!hallways || hallways.length === 0) {
      return {
        warehouse: warehouse as Warehouse,
        hallways: [],
        totalPositions: 0,
        occupiedPositions: 0,
        occupancyPercent: 0,
      };
    }

    // 3. Get all racks for these hallways
    const hallwayIds = hallways.map((h) => h.id);
    const { data: racks, error: racksError } = await supabase
      .from("wms_racks")
      .select("*")
      .in("hallway_id", hallwayIds)
      .eq("is_active", true)
      .order("sequence", { ascending: true });

    if (racksError) throw racksError;

    // 4. Get all positions for these racks
    const rackIds = (racks || []).map((r) => r.id);
    const { data: positions, error: positionsError } = await supabase
      .from("wms_positions")
      .select("*")
      .in("rack_id", rackIds)
      .eq("is_active", true)
      .order("level", { ascending: false })
      .order("code", { ascending: true });

    if (positionsError) throw positionsError;

    // 5. Get all inventory for these positions (batched to avoid URL length limits)
    const positionIds = (positions || []).map((p) => p.id);
    const inventory = await batchedInQuery<WmsInventory>(
      "wms_inventory",
      `*, product:products(id, short_name), pallet:wms_pallets(id, code, barcode, lot_number)`,
      "position_id",
      positionIds
    );

    // Group inventory by position
    const inventoryByPosition = new Map<string, WmsInventory[]>();
    for (const inv of inventory) {
      const existing = inventoryByPosition.get(inv.position_id) || [];
      existing.push(inv);
      inventoryByPosition.set(inv.position_id, existing);
    }

    // Group positions by rack
    const positionsByRack = new Map<string, Position[]>();
    for (const pos of positions || []) {
      const existing = positionsByRack.get(pos.rack_id) || [];
      existing.push(pos as Position);
      positionsByRack.set(pos.rack_id, existing);
    }

    // Group racks by hallway
    const racksByHallway = new Map<string, Rack[]>();
    for (const rack of racks || []) {
      const existing = racksByHallway.get(rack.hallway_id) || [];
      existing.push(rack as Rack);
      racksByHallway.set(rack.hallway_id, existing);
    }

    // Build the hierarchy
    let warehouseTotalPositions = 0;
    let warehouseOccupiedPositions = 0;

    const hallwayInventoryData: HallwayInventoryData[] = hallways.map(
      (hallway) => {
        const hallwayRacks = racksByHallway.get(hallway.id) || [];
        let hallwayTotalPositions = 0;
        let hallwayOccupiedPositions = 0;

        const rackInventoryData: RackInventoryData[] = hallwayRacks.map(
          (rack) => {
            const rackPositions = positionsByRack.get(rack.id) || [];
            let rackOccupiedPositions = 0;

            // NEUTRALIZED: reserved_boxes ignored
            const positionInventoryData: PositionInventoryData[] =
              rackPositions.map((position) => {
                const positionInventory =
                  inventoryByPosition.get(position.id) || [];
                const totalBoxes = positionInventory.reduce(
                  (sum, inv) => sum + (inv.box_count || 0),
                  0
                );
                const reservedBoxes = 0; // NEUTRALIZED
                const hasStock = totalBoxes > 0;

                if (hasStock) {
                  rackOccupiedPositions++;
                }

                return {
                  position,
                  inventory: positionInventory,
                  totalBoxes,
                  reservedBoxes,
                  availableBoxes: totalBoxes, // NEUTRALIZED: same as total
                  hasStock,
                };
              });

            hallwayTotalPositions += rackPositions.length;
            hallwayOccupiedPositions += rackOccupiedPositions;

            return {
              rack: rack as Rack,
              positions: positionInventoryData,
              totalPositions: rackPositions.length,
              occupiedPositions: rackOccupiedPositions,
              occupancyPercent:
                rackPositions.length > 0
                  ? Math.round(
                      (rackOccupiedPositions / rackPositions.length) * 100
                    )
                  : 0,
            };
          }
        );

        warehouseTotalPositions += hallwayTotalPositions;
        warehouseOccupiedPositions += hallwayOccupiedPositions;

        return {
          hallway: hallway as Hallway,
          racks: rackInventoryData,
          totalPositions: hallwayTotalPositions,
          occupiedPositions: hallwayOccupiedPositions,
          occupancyPercent:
            hallwayTotalPositions > 0
              ? Math.round(
                  (hallwayOccupiedPositions / hallwayTotalPositions) * 100
                )
              : 0,
        };
      }
    );

    return {
      warehouse: warehouse as Warehouse,
      hallways: hallwayInventoryData,
      totalPositions: warehouseTotalPositions,
      occupiedPositions: warehouseOccupiedPositions,
      occupancyPercent:
        warehouseTotalPositions > 0
          ? Math.round(
              (warehouseOccupiedPositions / warehouseTotalPositions) * 100
            )
          : 0,
    };
  },
};
